import React, { useState } from 'react';
import {
  Button,
  ConfigProvider,
  Divider,
  Flex,
  Modal,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useLocation } from 'react-use';

import RolloutDiff from '../RolloutDiff';
import { ChangelogState, ChangelogType } from '@/common/const';
import {
  Changelog,
  ModifiedRolloutTargetMapValue,
} from '@/common/dto/changelog';
import { VariableState } from '@/constants';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import { useVariable } from '@/contexts/VariableContext';
import { Rollout } from '@/lib/App';
import { UserAction } from '@/utils/accessControl';
import {
  defaultButtonProps,
  primaryButtonProps,
  secondaryButtonProps,
} from '@/utils/button';
import { getModifiedRolloutTargetsMap } from '@/utils/changelog';
import { formatUTCStringToDateTimeString } from '@/utils/dateTime';

interface OwnProps {
  changelog: Changelog;
  latestRollout?: Rollout;
  modifiedRolloutTargets: Map<string, ModifiedRolloutTargetMapValue>;
  onRestore?: (rollout: Rollout) => void;
}

function ChangelogItemStateCountTags({ modifiedRolloutTargets }) {
  if (!Array.from(modifiedRolloutTargets.keys()).length) return null;

  const tags = [];

  const stateCount = {
    [ChangelogState.ADDED]: 0,
    [ChangelogState.DELETED]: 0,
    [ChangelogState.MODIFIED]: 0,
  };

  modifiedRolloutTargets.forEach((value) => {
    stateCount[value.state] = stateCount[value.state] + 1;
  });

  for (let k in stateCount) {
    if (stateCount[k]) {
      let color;

      switch (k) {
        case ChangelogState.ADDED:
          color = 'green';
          break;
        case ChangelogState.DELETED:
          color = 'red';
          break;
        case ChangelogState.MODIFIED:
          color = 'orange';
          break;
        default:
          color = 'default';
      }

      tags.push(
        <Tag
          key={`changelog-collapse-title-tag-${k}`}
          data-testid={`changelog-collapse-title-tag-${k}`}
          color={color}
          size="small"
          bordered={false}
          style={{
            width: 28,
            textAlign: 'center',
          }}
        >
          {stateCount[k]}
        </Tag>,
      );
    }
  }

  return (
    <Flex align="center" gap={4}>
      <ConfigProvider
        theme={{
          token: {
            marginXS: 0,
          },
        }}
      >
        {tags}
      </ConfigProvider>
    </Flex>
  );
}

function RollbackContent({ changelog, latestRollout }) {
  switch (changelog?.changelog_type) {
    case ChangelogType.ROLLOUT: {
      const modifiedRolloutTargets = getModifiedRolloutTargetsMap(
        changelog?.rollout?.newRollout as Rollout,
        latestRollout,
      );
      return (
        <RolloutDiff
          data-testid="changeog-collapse-title-rollout-diff"
          rolloutTargets={modifiedRolloutTargets}
          changelog={changelog}
        />
      );
    }
    default:
      return <></>;
  }
}

function RollbackConfirmationModal({
  changelog,
  latestRollout,
  showRollbackConfirm,
  onCancel,
  onOk,
}) {
  if (!showRollbackConfirm) return <></>;

  return (
    <Modal
      centered
      open={showRollbackConfirm}
      title="Rollback Version"
      cancelButtonProps={defaultButtonProps}
      onCancel={onCancel}
      okButtonProps={primaryButtonProps}
      onOk={onOk}
      styles={{
        body: {
          height: 'fit-content',
          overflow: 'hidden',
        },
      }}
    >
      <Flex
        vertical
        gap={8}
        style={{
          padding: 16,
          overflow: 'hidden auto',
          height: '100%',
        }}
      >
        <Typography.Text>
          Are you sure that you want to rollback to the version approved and
          applied on
          <Typography.Text strong>
            {` ${formatUTCStringToDateTimeString(changelog?.updated_at)} `}
          </Typography.Text>
          by
          <Typography.Text strong>
            {` ${changelog?.updated_by}`}
          </Typography.Text>
          ? This will result in losing all unsaved changes.
        </Typography.Text>
        <RollbackContent changelog={changelog} latestRollout={latestRollout} />
      </Flex>
    </Modal>
  );
}

/**
 * Render this component to show the rollback button in variable rollout page only
 * In global changelog, we are unable to get the latest rollout version of each variable
 * @returns
 */
function RollbackChangelog({ changelog, latestRollout, onRestore }) {
  const [showRollbackConfirm, setShowRollbackConfirm] =
    useState<boolean>(false);

  const variable = useVariable();

  const { permissionUtils } = useUserRolesContext();

  if (changelog?.changelog_type !== ChangelogType.ROLLOUT || !latestRollout)
    return <></>;

  if (latestRollout?.version !== changelog?.rollout?.newRollout?.version)
    return (
      <>
        <Flex gap={8} align="center">
          <Button
            {...secondaryButtonProps}
            onClick={async (e) => {
              e.stopPropagation();

              setShowRollbackConfirm(true);
            }}
            disabled={
              variable.state === VariableState.ARCHIVED ||
              !permissionUtils.canPerformAction(
                UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
                [variable.techFamily],
              )
            }
          >
            Rollback Version
          </Button>
          <Divider type="vertical" />
        </Flex>
        <RollbackConfirmationModal
          showRollbackConfirm={showRollbackConfirm}
          changelog={changelog}
          latestRollout={latestRollout}
          onCancel={() => {
            setShowRollbackConfirm(false);
          }}
          onOk={() => {
            setShowRollbackConfirm(false);

            onRestore(changelog?.rollout?.newRollout as Rollout);
          }}
        />
      </>
    );

  return <></>;
}

export default function ChangelogCollapseTitle({
  changelog,
  latestRollout,
  modifiedRolloutTargets,
  onRestore = () => {},
}: OwnProps) {
  const location = useLocation();
  const isRolloutPage = location.pathname.match(/\/variables\/.*\/rollout/);

  function getChangelogCollapseTitle(): string {
    switch (changelog?.changelog_type) {
      case ChangelogType.ROLLOUT:
        return 'View Rollout Changes';
      case ChangelogType.VARIABLE:
        return 'View Variable Details Changes';
      default:
        return '';
    }
  }

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        style={{
          height: 32,
        }}
      >
        <Flex align="center" gap={8}>
          <Tooltip title={getChangelogCollapseTitle()}>
            <Typography.Text strong>
              {getChangelogCollapseTitle()}
            </Typography.Text>
          </Tooltip>

          <ChangelogItemStateCountTags
            modifiedRolloutTargets={modifiedRolloutTargets}
          />
        </Flex>

        {isRolloutPage && (
          <RollbackChangelog
            changelog={changelog}
            latestRollout={latestRollout}
            onRestore={onRestore}
          />
        )}
      </Flex>
    </>
  );
}
